/**
 * `diagram/vanillaRender.ts` — the vanilla mermaid → SVG render path (interactive-diagrams-v0.1.md
 * §6 R1). Extracted verbatim from the old `PreviewPane` mermaid post-processing so the
 * zero-regression path (§5.4: any diagram without an `@aletheia` sidecar) renders *exactly* as
 * it did before this feature — same init config, same `mermaid.render` call, same failure mode.
 *
 * This is the fallback arm of the read-path decision tree: non-flowchart diagrams, sidecar-less
 * flowcharts, and parse failures all come here. The interactive `DiagramCanvas` is only used for
 * flowcharts that actually carry saved geometry.
 *
 * `mermaid` is heavy (~500 KB) and pulls the same chunk the graph surface already vendors, so it
 * is imported normally (the editor bundle already paid for it). Init is idempotent.
 */

import mermaid from 'mermaid';

let initialized = false;

/** The app UI font (tokens.css `--font-ui`) as a concrete stack — mermaid renders into an SVG and
 *  reads a literal family from config, so we can't hand it a CSS var. */
const DIAGRAM_FONT = "'IBM Plex Sans', 'Source Sans 3', ui-sans-serif, system-ui, sans-serif";

/**
 * Presentation config for the vanilla-mermaid render (professional-polish P3, 2026-07-08).
 * `theme: 'base'` so `themeVariables` fully apply (node fills come from each diagram's `classDef`,
 * so overriding the base palette only affects un-classed nodes + chrome). We tune the chrome to a
 * modern slate look: the app sans (kills mermaid's dated Trebuchet default), a neutral edge/line
 * color, tinted rounded subgraph containers, and roomier flowchart spacing. `htmlLabels` +
 * `useMaxWidth` let long labels wrap instead of overflowing. `securityLevel: 'strict'` is kept —
 * diagram source is user content, so mermaid must sanitize label HTML.
 */
const MERMAID_CONFIG = {
  startOnLoad: false,
  theme: 'base' as const,
  securityLevel: 'strict' as const,
  fontFamily: DIAGRAM_FONT,
  themeVariables: {
    fontFamily: DIAGRAM_FONT,
    fontSize: '14px',
    lineColor: '#64748b',
    // Un-classed default node (rare — most nodes are classDef'd), kept a neutral light slate.
    primaryColor: '#f1f5f9',
    primaryBorderColor: '#94a3b8',
    primaryTextColor: '#0f172a',
    // Subgraph containers: subtle tint + soft border, matching the canvas frames.
    clusterBkg: '#f8fafc',
    clusterBorder: '#cbd5e1',
    titleColor: '#0f172a',
    edgeLabelBackground: '#ffffff',
    // Gantt: status colors match the diagram palette (active=blue, done=green, crit=red) on a
    // light slate base with subtly banded sections and a legible task text.
    sectionBkgColor: '#f1f5f9',
    altSectionBkgColor: '#e2e8f0',
    sectionBkgColor2: '#f8fafc',
    taskBkgColor: '#e2e8f0',
    taskBorderColor: '#94a3b8',
    taskTextColor: '#0f172a',
    taskTextDarkColor: '#0f172a',
    taskTextOutsideColor: '#334155',
    activeTaskBkgColor: '#dbeafe',
    activeTaskBorderColor: '#2563eb',
    doneTaskBkgColor: '#dcfce7',
    doneTaskBorderColor: '#16a34a',
    critBkgColor: '#fee2e2',
    critBorderColor: '#dc2626',
    gridColor: '#e2e8f0',
    todayLineColor: '#ea580c',
  },
  flowchart: {
    htmlLabels: true,
    useMaxWidth: true,
    curve: 'basis' as const,
    nodeSpacing: 48,
    rankSpacing: 58,
    padding: 14,
  },
  gantt: {
    useMaxWidth: true,
    barHeight: 22,
    barGap: 5,
    topPadding: 44,
    leftPadding: 88,
    gridLineStartPadding: 36,
    fontSize: 12,
    sectionFontSize: 13,
    numberSectionStyles: 3,
  },
};

/** Initialize mermaid once with the presentation config. Idempotent. */
export function initMermaid(): void {
  if (initialized) return;
  mermaid.initialize(MERMAID_CONFIG);
  initialized = true;
}

/** Monotonic id source for mermaid render targets (mermaid requires a unique id per render). */
let seq = 0;

/** The result of a vanilla render — SVG markup on success, an error message on failure. */
export type VanillaRenderResult = { ok: true; svg: string } | { ok: false; error: string };

/**
 * Render mermaid source to an SVG string. Never throws — a syntax error (or any mermaid
 * failure) is returned as `{ ok: false, error }` so the caller can fall back to showing the
 * raw source with an error marker (the original behavior). Async because `mermaid.render` is.
 *
 * @param source - The mermaid fence body.
 * @param idPrefix - Optional prefix for the generated render id (default `mermaid`).
 */
export async function renderMermaid(source: string, idPrefix = 'mermaid'): Promise<VanillaRenderResult> {
  initMermaid();
  const id = `${idPrefix}-${(seq += 1)}`;
  try {
    const { svg } = await mermaid.render(id, source);
    return { ok: true, svg };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
